import React, { useState, useRef, useEffect } from 'react'
import classes from './VideoPlayer.module.css'
import classNames from 'classnames'
import { ReactComponent as PlayIcon } from '../../assets/icons/play.svg'
import { ReactComponent as PauseIcon } from '../../assets/icons/pause.svg'
import { ReactComponent as VolumeHigh } from '../../assets/icons/volume-high.svg'
import { ReactComponent as VolumeLow } from '../../assets/icons/volume-low.svg'
import { ReactComponent as VolumeMute } from '../../assets/icons/volume-off.svg'
import { ReactComponent as VolumeOff } from '../../assets/icons/volume-mute.svg'
import FastForward from '../../assets/icons/fast-forward.svg'
import Rewind from '../../assets/icons/rewind.svg'
import VolumeSlider from './VolumeSlider'

export const VideoPlayer = ({
    sources,
    onStart,
    onProgressUpdate,
    onComplete,
    poster,
    autoplay = false,
    preload = 'metadata',
    className,
    ...props
}) => {
    const [playing, setPlaying] = useState(false)
    const [muted, setMuted] = useState(false)
    const [progress, setProgress] = useState(0)
    const [bufferedProgress, setBufferedProgress] = useState(0)
    const [volume, setVolume] = useState(1)
    const [duration, setDuration] = useState(0)
    const [showControls, setShowControls] = useState(false)
    const [started, setStarted] = useState(false)
    const videoRef = useRef(null)

    const updateDuration = event => {
        const { target: video } = event
        setDuration(video.duration)
    }

    const maybeAutoplay = event => {
        const { target: video } = event
        if (autoplay) video.play()
    }

    const updateProgress = event => {
        const { target: video } = event
        const progress = video.currentTime / video.duration
        setProgress(progress)
        if (typeof onProgressUpdate === 'function')
            onProgressUpdate({
                progress,
                duration,
            })
    }

    const updateBufferedProgress = event => {
        const { target: video } = event
        try {
            setBufferedProgress(video.buffered.end(0) / video.duration)
        } catch (err) {}
    }

    const play = () => {
        const { current: video } = videoRef
        video.play()
    }

    const pause = () => {
        const { current: video } = videoRef
        video.pause()
    }

    const skip = value => {
        const { current: video } = videoRef
        video.currentTime += value
    }

    const mute = () => {
        const { current: video } = videoRef
        video.muted = !video.muted
        setMuted(video.muted)
    }

    const handlePlay = event => {
        const { target: video } = event
        const start = video.currentTime === 0
        setPlaying(true)
        if (start) {
            setStarted(true)
            if (typeof onStart === 'function') onStart()
        }
    }

    const maybeShowControls = () => {
        // We don't want to show the controls when the video hasn't been plaued
        // at all, and the thumbnail is still showing
        if (started) setShowControls(true)
    }

    const hideControls = () => {
        if (showControls) setShowControls(false)
    }

    // Source: https://stackoverflow.com/a/25279340
    const formatTimestamp = seconds =>
        new Date(seconds * 1000).toISOString().substr(11, 8)

    const progressInSeconds = progress * duration

    let volumeIcon = volume >= 0.5 ? <VolumeHigh /> : <VolumeLow />
    if (volume === 0) volumeIcon = <VolumeOff />

    useEffect(() => {
        const { current: video } = videoRef
        if (video) video.volume = volume
    }, [volume])

    return (
        <div
            className={classNames(classes.playerContainer, className)}
            onMouseEnter={maybeShowControls}
            onMouseLeave={hideControls}
        >
            <video
                className={classes.player}
                {...props}
                ref={videoRef}
                poster={poster}
                autoplay={autoplay}
                controls
                onLoadedMetadata={updateDuration}
                onDurationChange={updateDuration}
                onCanPlay={maybeAutoplay}
                onTimeUpdate={updateProgress}
                onProgress={updateBufferedProgress}
                onPlay={handlePlay}
                onPause={() => setPlaying(false)}
                preload={preload}
                volume={volume}
                controlsList="nodownload"
            >
                {sources.map(source => (
                    <source src={source.src} type={source.type} />
                ))}
                <p>
                    Your browser doesn't support HTML5 video. Here is a{' '}
                    <a href={sources && sources[0] && sources[0].src}>
                        link to the video
                    </a>{' '}
                    instead.
                </p>
            </video>
            {!started && (
                <div className={classes.playerOverlay}>
                    <div
                        className={classes.playerPoster}
                        style={{ backgroundImage: `url(${poster})` }}
                    >
                        <button
                            type="button"
                            className={classes.posterPlayButton}
                            onClick={play}
                        >
                            <PlayIcon />
                        </button>
                    </div>
                    {/* <div
                        className={classNames(
                            classes.playerControlsWrapper,
                            showControls && classes.playerControlsWrapperShown
                        )}
                    >
                        <div className={classes.playerTrack}>
                            <div
                                className={classNames(
                                    classes.playerTrackSegment,
                                    classes.playerTrackSegmentPlayed
                                )}
                                style={{
                                    transform: `scaleX(${progress})`,
                                }}
                            />
                            <div
                                className={classNames(
                                    classes.playerTrackSegment,
                                    classes.playerTrackSegmentBuffered
                                )}
                                style={{
                                    transform: `scaleX(${bufferedProgress})`,
                                }}
                            />
                        </div>
                        <div className={classes.playerControls}>
                            <button
                                className={classNames(
                                    classes.controlButton,
                                    classes.playButton
                                )}
                                type="button"
                                aria-label="rewind"
                                onClick={() => skip(-10)}
                            >
                                <img src={Rewind} style={{width: 18, height: 18}}/>
                            </button>
                            <button
                                className={classNames(
                                    classes.controlButton,
                                    classes.playButton
                                )}
                                type="button"
                                aria-label={playing ? 'Pause video' : 'Play video'}
                                onClick={playing ? pause : play}
                            >
                                {playing ? <PauseIcon /> : <PlayIcon />}
                            </button>
                            <button
                                className={classNames(
                                    classes.controlButton,
                                    classes.playButton
                                )}
                                type="button"
                                aria-label="rewind"
                                onClick={() => skip(10)}
                            >
                                <img src={FastForward} style={{width: 18, height: 18}}/>
                            </button>
                            <button
                                className={classNames(
                                    classes.controlButton,
                                    classes.volumeButton
                                )}
                                type="button"
                                aria-label="Mute"
                                onClick={mute}
                            >
                                {muted ? <VolumeMute /> : volumeIcon}
                            </button>
                            <VolumeSlider
                                min={0}
                                max={1}
                                step={0.1}
                                value={volume}
                                onChange={value => setVolume(value)}
                                className={classes.volumeSlider}
                            />
                            <span className={classes.videoProgressText}>
                                <span className={classes.videoProgressTextPlayed}>
                                    {formatTimestamp(progressInSeconds)}
                                </span>
                                /{formatTimestamp(duration)}
                            </span>
                        </div>
                    </div> */}
                </div>
            )}
        </div>
    )
}
